import { useState, useEffect } from "react"

import dayjs from "dayjs"

import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

import { 
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	Divider,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	ListSubheader,
	IconButton
} from "@mui/material"

import AddIcon from "@mui/icons-material/Add"

import ParameterFiller from "../../../Crm/Templates/ParameterFiller"

import { getSession } from "../../../../auth"
import api from "../../../../api"
import useNotification from '../../../Notification';

import "./styles.css"
import "dayjs/locale/pt-br"

dayjs.locale("pt-br")


const emptyEvent = () => {
	return { 
		event: null,
		event_name: "",
		send_time: null,
		send_offset_time: null,
		message_type: "WHATSAPP",
		message_content: null,
		message_parameters: {},
		sender: null
	}
}

const predefinedOffsets = [
	{ label: "5 minutos", value: "00:05:00" },
	{ label: "10 minutos", value: "00:10:00" },
	{ label: "15 minutos", value: "00:15:00" },
	{ label: "30 minutos", value: "00:30:00" },
	{ label: "1 hora", value: "01:00:00" },
	{ label: "Personalizado", value: "custom" },
]

const EditEventDialog = ({ event, templates, close, update, remove }) => {

	const [events, setEvents] = useState([])
	const [customEvents, setCustomEvents] = useState([])
	const [senders, setSenders] = useState([])
	const [requiredParameters, setRequiredParameters] = useState([])
	const [paramsReady, setParamsReady] = useState(true)
	const [template, setTemplate] = useState(templates.find((template) => template.name === event.message_content))
	
	const [showCustomOffset, setShowCustomOffset] = useState(() => {
		if (Object.keys(event).length === 0)
			return false
		return ! predefinedOffsets.map((obj) => obj.value).includes(event.send_offset_time)
	})
	
	const [state, setState] = useState(
		(Object.keys(event).length !== 0)
			? { 
				...event, 
				send_time: (event.send_time) ? dayjs(event.send_time, "HH:mm:ss") : event.send_time,
				send_offset_time: (event.send_offset_time) ? dayjs(event.send_offset_time, "HH:mm:ss") : event.send_offset_time
			}
			: emptyEvent()
	)

	const [showCreateNewEvent, setShowCreateNewEvent] = useState(false)
    const [newEventName, setNewEventName] = useState("")

    const matchRegLen = (value) => {
        if (!value)
            return
        const matcher = value.match(/{{\d+}}/g)
        if (matcher)
            return matcher.length
        return 0
    }

	const checkRequiredParameters = (template) => {
		if (! template) return

		const parameters = []
        if (template.components.length == 1 && template.components[0].type == "BODY") {
            let amount = matchRegLen(template.components[0].text)
            if (amount)
                parameters.push(
                    {
                        type: "BODY",
                        amount: amount
                    }
                )
        }
        else {
            template.components.map(component => {
                let amount = 0
                if (component.type == "HEADER") {
                    amount = 1
                    if (component.format == "TEXT")
                        amount = matchRegLen(component.text)
                    if (amount)
                        parameters.push({
                            type: component.type,
                            format: component.format,
                            amount: amount
                        })
                }
                else if (component.type == "BODY") {
                    amount = matchRegLen(component.text)
                    if (amount)
                        parameters.push(
                            {
                                type: "BODY",
                                amount: amount
                            }
                        )
                }

                else if (component.type == "BUTTONS") {
                    amount = 0
                    component.buttons.map(button => {
                        if (button.type === "URL")
                            amount += matchRegLen(button.url)
                    })
                    if (amount)
                        parameters.push(
                            {
                                type: "BUTTONS",
                                amount: amount
                            }
                        )
                }

            })
        }
        setRequiredParameters(parameters)
        setParamsReady(parameters.length === 0)
	}

	const checkParametersReady = () => {
		setParamsReady(requiredParameters.every(param => {
			if (param.type === "HEADER" && param.format === "IMAGE")
				return !!state.message_parameters.headerURL
			if (param.type === "BODY") {
				for (let i = 0; i < param.amount; i++) {
					if (!state.message_parameters.body || !state.message_parameters.body[i]) 
						return false
				}
				return true
			}
			return true
		}))
	}

	useEffect(() => {
		const fetchData = async () => {
			const enterpriseId = getSession().profile.enterprise.id
			const [sendersResponse, eventsResponse, customEventsResponse] = await Promise.all([
				api.get(`api/enterprises/${enterpriseId}/whatsapp/`),
				api.get(`api/message_events/`),
                api.get(`api/enterprises/${enterpriseId}/custom_events/`)
			])
			setSenders((sendersResponse.status === 200) ? sendersResponse.data.map((row) => row.phone_number) : [])
			setEvents((eventsResponse.status === 200) ? eventsResponse.data : [])
			setCustomEvents((customEventsResponse.status === 200) ? customEventsResponse.data : [])
		}
		fetchData()
		checkRequiredParameters(templates.find((template) => template.name === event.message_content))
	}, [])

	const createNewEvent = async (name) => {
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.post(`api/enterprises/${enterpriseId}/custom_events/`, {
            name       : name,
            identifier : name.toUpperCase(),
            type       : "Agenda"
        })
        if (response.status === 201) {
            useNotification("Sucesso", "Evento customizado adicionado com sucesso.", "success")
            setCustomEvents((prevState) => [ ...prevState, response.data ])
            setShowCreateNewEvent(false)
            return
        }
        useNotification("Ops", "Algo deu errado, tente novamente.", "danger")
    }

	return (
		<Dialog open={true} onClose={close} fullWidth maxWidth="sm">
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
				Configuração de disparo automático
				<Button onClick={() => window.open("https://instant.getoutline.com/s/canal-do-cliente/doc/configurando-templates-para-utilizacao-em-disparos-automaticos-VFNWNz6AKa", "_blank")}>FAQ</Button>
			</DialogTitle>
			<DialogContent className="edit-event-content">
				{showCreateNewEvent &&
                    <Dialog open={showCreateNewEvent} onClose={() => setShowCreateNewEvent(false)}>
                        <DialogTitle>Novo tipo</DialogTitle>
                        <DialogContent sx={{ paddingTop: ".5rem !important" }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Tipo"
                                value={newEventName}
                                onChange={(e) => setNewEventName(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowCreateNewEvent(false)}>Cancelar</Button>
                            <Button onClick={() => createNewEvent(newEventName)} disabled={!newEventName.trim()}>
                                Salvar
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
				<FormControl fullWidth size="small">
  					<InputLabel>Evento</InputLabel>
					<Select
						value={state.event || ""}
						label="Evento"
						onChange={(event) => setState((prevState) => ({ 
							...prevState, 
							event: event.target.value,
							event_name: [...events, ...customEvents].find((e) => e.identifier === event.target.value)?.name || ""
						}))}
						disabled={event.event}
					>
						<MenuItem value="BIRTHDAY">Aniversário</MenuItem>
						<Divider />
						<ListSubheader>Sistema</ListSubheader>
                        {events.filter((event) => event.identifier !== "BIRTHDAY").map((event) => (
                            <MenuItem key={event.identifier} value={event.identifier}>{event.name}</MenuItem>    
                        ))}
                        <Divider />
                        <ListSubheader sx={{ display: "flex", gap: ".5rem" }}>
                            Customizados
                            <IconButton onClick={() => setShowCreateNewEvent(true)}>
                                <AddIcon />
                            </IconButton>
                        </ListSubheader>
                        {customEvents.map((event) => (
                            <MenuItem key={event.identifier} value={event.identifier}>{event.name}</MenuItem>
                        ))}
  					</Select>
				</FormControl>
				<FormControl fullWidth size="small">
  					<InputLabel>Número de origem</InputLabel>
  					<Select
						value={state.sender || ""}
						label="Número de origem"
						onChange={(event) => setState((prevState) => ({ 
							...prevState, 
							sender: event.target.value
						}))}
					>
						{senders.map((sender) => (
    						<MenuItem value={sender}>{sender}</MenuItem>
						))}
  					</Select>
				</FormControl>
				<Box sx={{ display: "flex", gap: "1rem" }}>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
						<TimePicker
							label="Horário de envio"
							value={state.send_time}
							onChange={(newValue) => setState((prevState) => ({ ...prevState, send_time: newValue }))}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
							disabled={events.find((event) => event.identifier == state.event)?.name !== "Aniversário"}
						/>
					</LocalizationProvider>
					{! showCustomOffset &&
						<FormControl size="small" fullWidth>
							<InputLabel>Enviar com antecedência de</InputLabel>
							<Select
								value={state.send_offset_time?.format("HH:mm:ss") || ""}
								label="Enviar com antecedência de"
								onChange={(event) => {
									if (event.target.value !== "custom")
										setState((prevState) => ({ ...prevState, send_offset_time: dayjs(event.target.value, "HH:mm:ss") }))
									else
										setShowCustomOffset(true)
								}}
								disabled={events.find((event) => event.identifier == state.event)?.name === "Aniversário"}
							>
								{predefinedOffsets.map((opt) => (
									<MenuItem key={opt.label} value={opt.value}>
										{opt.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					}
					{showCustomOffset &&
						<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
							<TimePicker
								label="Enviar com antecedência de"
								value={state.send_offset_time}
								onChange={(newValue) => setState((prevState) => ({ ...prevState, send_offset_time: newValue }))}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
								disabled={events.find((event) => event.identifier == state.event)?.name === "Aniversário"}
							/>
						</LocalizationProvider>
					}
				</Box>
				<Box className="event-parameter-filler-header">Template</Box>
				{event.message_content 
					? (
						<TextField fullWidth
							size="small"
							label="Template"
							value={state.message_content}
							disabled={true}
						/>
					)
					: (
						<FormControl fullWidth size="small">
		  					<InputLabel>Template</InputLabel>
							<Select
								value={template?.name || ""}
								label="Template"
								onChange={(event) => {
									const template = templates.find((template) => template.name === event.target.value)
									setTemplate(template)
									checkRequiredParameters(template)
									setState((prevState) => ({ 
										...prevState, 
										message_content: event.target.value
									}))
								}}
							>
								{templates.map((template) => (
		    						<MenuItem value={template.name}>{template.name}</MenuItem>
								))}
		  					</Select>
						</FormControl>
					)
				}
				{requiredParameters.length !== 0 &&
					<Box className="event-parameter-filler">
						<Box className="event-parameter-filler-header">Preencha os parâmetros do template abaixo</Box>
						<ParameterFiller
	                        mediaValues={template.components}
	                        parameters={requiredParameters}
	                        submitParameters={state.message_parameters}
	                        setReady={() => checkParametersReady()}
	                    />
	                </Box>
				}
			</DialogContent>
			<DialogActions>
				{state.id && <Button sx={{ color: "#c95f50" }} onClick={() => { remove(state); close() }}>Excluir</Button>}
				<Button onClick={() => close()}>Cancelar</Button>
				<Button onClick={() => { update(state); close() }} disabled={! state.event || (! state.send_time && ! state.send_offset_time) || ! state.sender || ! paramsReady}>Salvar</Button>
			</DialogActions>
		</Dialog>
	)
}

export default EditEventDialog
